import { TAcquisitionProviderFacade } from "../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";

import { isNewErrorMessagingFlagActive } from './error-message-flag';
import { cleanNumericInput } from './income-utils';
import SessionStorage from "./SessionStorage";

export const generateFieldErrorMessage = (fieldName: string, mode?: 'missingValue' | 'thirdParty', subLabel?: string) => {
    return generateErrorMessage(fieldName, mode, subLabel, true);
}

export const generateErrorMessage = (fieldName: string, mode?: 'missingValue' | 'thirdParty', subLabel?: string, isField?: boolean): string => {
    if (!isNewErrorMessagingFlagActive) {
        if (isField) {
            return `Please provide a valid ${fieldName}`;
        }

        if (fieldName.includes('Authorized User')) {
            return `Please provide the ${fieldName}`;
        }

        return `Please provide your ${fieldName}`;
    }

    if (mode === 'missingValue') {
        if(/income/ig.test(fieldName) && SessionStorage.tryGetObject('grossIncomeEnabled', false)) {
            return `${fieldName.replace(/Total Monthly/, '')} is required`;
        }

        return `${fieldName} is required`;
    }

    if (mode === 'thirdParty') {
        return `We're unable to verify this ${fieldName}. Please verify and re-enter your ${subLabel}`;
    }

    return `A valid ${fieldName} is required`;
};

export const generateMonthlyIncomeErrorMessage = (fieldName: string, value: string): string => {
    const income = cleanNumericInput(value);

    if(isNewErrorMessagingFlagActive && SessionStorage.tryGetObject('grossIncomeEnabled', false) ) {
        return `${fieldName.replace('Total Monthly Income', 'Income')} is required`;
    }
    else if (isNewErrorMessagingFlagActive && income && Number(income) < 100) {
        return `${fieldName.replace('Total Monthly Income', 'Monthly Income')} must be at least 3 digits`;
    }
    return generateErrorMessage(fieldName, income ? undefined : 'missingValue');
};

export const generateEmailFormatErrorMessage = (fieldName: string): string => {
    if (!isNewErrorMessagingFlagActive) {
        return `Please provide a valid ${fieldName}: email@domain.com`;
    } 

    return `A valid ${fieldName} using '@' and a valid domain and extension (ex: .com) is required`;
}

export const generatePersonalInformationErrorMap = (
    acquisitionData: ReturnType<TAcquisitionProviderFacade['getPersonalInformation']>,
    personalInfoErrors: ReturnType<TAcquisitionProviderFacade['getPersonalInfoErrors']>,
    homeAddressPrimaryRef: ReturnType<TAcquisitionProviderFacade['getHomeAddressPrimaryRef']>, 
    getFeatureFlags: ReturnType<TAcquisitionProviderFacade['getCurrentMetaAcquisitionFlowState']>['getFeatureFlags']
): Record<string, string> => {
    let toReturnErrorMap: Record<string, string> = {};
    if (homeAddressPrimaryRef.current.value) {
        toReturnErrorMap = {
            mailingAddressLineOne:
                generateErrorMessage('<a href="#mailingAddress-field-container" id="cta-mailingAddress">Home Address</a>', !acquisitionData.mailingAddressLineOne.value ? 'missingValue': 'thirdParty', 'address'),
            mailingAddressCity:
                generateErrorMessage('<a href="#mailingCity-field-container" id="cta-mailingCity">Home Address City</a>', !acquisitionData.mailingAddressCity.value ? 'missingValue' : null),
            mailingAddressState:
                generateErrorMessage('<a href="#mailingState-field-container" id="cta-mailingState">Home Address State</a>', !acquisitionData.mailingAddressState.value ? 'missingValue' : null),
            mailingAddressZipCode:
                generateErrorMessage('<a href="#mailingZipCode-field-container" id="cta-mailingZipCode">Home Address ZIP Code</a>', !acquisitionData.mailingAddressZipCode.value ? 'missingValue' : null),
            homeAddressLineOne:
                generateErrorMessage('<a href="#homeAddress-field-container" id="cta-homeAddress">Mailing Street Address</a>', !acquisitionData.homeAddressLineOne.value ? 'missingValue' : 'thirdParty', 'address'),
            homeAddressCity:
                generateErrorMessage('<a href="#homeCity-field-container" id="cta-homeCity">Mailing Address City</a>', !acquisitionData.homeAddressCity.value ? 'missingValue' : null),
            homeAddressState:
                generateErrorMessage('<a href="#homeState-field-container" id="cta-homeState">Mailing Address State</a>', !acquisitionData.homeAddressState.value ? 'missingValue' : null),
            homeAddressZipCode:
                generateErrorMessage('<a href="#homeZipCode-field-container" id="cta-homeZipCode">Mailing Address ZIP Code</a>', !acquisitionData.homeAddressZipCode.value ? 'missingValue' : null),
        };
    }

    const emailErrorType = getFeatureFlags('DPM-13566')
        ? acquisitionData.emailAddress.errorType
        : personalInfoErrors.EmailAddress;
    const phoneError = getFeatureFlags('DPM-14217')
        ? acquisitionData.phoneNumber.errorType
        : personalInfoErrors.PrimaryPhoneNumber;
    let phoneErrorType: 'missingValue' | 'thirdParty' = 'thirdParty';
    if (phoneError === 'requiredField' || phoneError === 'RequiredField') {
        if (acquisitionData.phoneNumber.value) {
            phoneErrorType = undefined;
        } else {
            phoneErrorType = 'missingValue';
        }
    } else if (phoneError === 'invalidInput' || phoneError === 'NotValid') {
        phoneErrorType = undefined;
    }

    toReturnErrorMap = {
        firstName:
            generateErrorMessage('<a href="#firstName-field-container" id="cta-firstName">First Name</a>', !acquisitionData.firstName.value ? 'missingValue' : null),
        lastName:
            generateErrorMessage('<a href="#lastName-field-container" id="cta-lastName">Last Name</a>', !acquisitionData.lastName.value ? 'missingValue' : null),
        suffix: generateErrorMessage('<a href="#suffix-field-container">Suffix</a>'),
        mailingAddressLineOne:
            generateErrorMessage('<a href="#mailingAddress-field-container" id="cta-mailingAddress">Mailing Address</a>', !acquisitionData.mailingAddressLineOne.value ? 'missingValue': 'thirdParty', 'address'),
        mailingAddressCity:
            generateErrorMessage('<a href="#mailingCity-field-container" id="cta-mailingCity">Mailing Address City</a>', !acquisitionData.mailingAddressCity.value ? 'missingValue' : null),
        mailingAddressState:
            generateErrorMessage('<a href="#mailingState-field-container" id="cta-mailingState">Mailing Address State</a>', !acquisitionData.mailingAddressState.value ? 'missingValue' : null),
        mailingAddressZipCode:
            generateErrorMessage('<a href="#mailingZipCode-field-container" id="cta-mailingZipCode">Mailing Address ZIP Code</a>', !acquisitionData.mailingAddressZipCode.value ? 'missingValue' : null),
        homeAddressLineOne:
            generateErrorMessage('<a href="#homeAddress-field-container" id="cta-homeAddress">Home Street Address</a>', !acquisitionData.homeAddressLineOne.value ? 'missingValue' : 'thirdParty', 'address'),
        homeAddressCity:
            generateErrorMessage('<a href="#homeCity-field-container" id="cta-homeCity">Home Address City</a>', !acquisitionData.homeAddressCity.value ? 'missingValue' : null),
        homeAddressState:
            generateErrorMessage('<a href="#homeState-field-container" id="cta-homeState">Home Address State</a>', !acquisitionData.homeAddressState.value ? 'missingValue' : null),
        homeAddressZipCode:
            generateErrorMessage('<a href="#homeZipCode-field-container" id="cta-homeZipCode">Home Address ZIP Code</a>', !acquisitionData.homeAddressZipCode.value ? 'missingValue' : null),
        emailAddress: emailErrorType === 'RequiredField' || emailErrorType === 'requiredField'
            ? generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'missingValue') + ': email@domain.com'
            : emailErrorType === 'NotValid' || emailErrorType === 'invalidInput'
                ? generateEmailFormatErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>')
                : generateErrorMessage('<a href="#emailAddress-field-container" id="cta-emailAddress">Email Address</a>', 'thirdParty', 'Email'),
        phoneNumber:
            generateErrorMessage('<a href="#phone-field-container" id="cta-phone">Phone Number</a>', phoneErrorType, 'Number'),
        monthlyIncome:
            generateMonthlyIncomeErrorMessage('<a href="#monthlyIncome-field-container" id="cta-monthlyIncome">Total Monthly Income</a>', acquisitionData.monthlyIncome.value || ''),
        ssn: generateErrorMessage('<a href="#socialSecurity-field-container" id="cta-socialSecurity">Social Security Number</a>', !acquisitionData.ssn.value ? 'missingValue' : null),
        dob: generateErrorMessage('<a href="#dob-field-container" id="cta-dob">Date of Birth</a>', !acquisitionData.dateOfBirthYear.value ? 'missingValue' : null),
        ...toReturnErrorMap,
    };

    return toReturnErrorMap;
}


export const generateMiscErrorMap = (
    acquisitionData: ReturnType<TAcquisitionProviderFacade['getPersonalInformation']>
): Record<string, string> => ({
        cardDesign:
            isNewErrorMessagingFlagActive ? '<a href="#plastic-card-design" id="cta-plastic-card-design">Card Design</a> selection is required' : 'Please select a <a href="#plastic-card-design" id="cta-plastic-card-design">Card Design</a>',
        creditProtectionInitials:
            isNewErrorMessagingFlagActive ? 'Please provide your <a href="#creditProtection-field-container" id="cta-creditProtection">initials to confirm enrollment</a>' : 'Please provide your <a href="#creditProtection-field-container" id="cta-creditProtection">Initials to confirm enrollment of Credit Protection</a>',
        authorizedUserFirstName:
            '<a href="#auFirstName-field-container" id="cta-auFirstName">Authorized User\'s First Name</a>',
        authorizedUserLastName:
            '<a href="#auLastName-field-container" id="cta-auLastName">Authorized User\'s Last Name</a>',
        authorizedUserDob:
            isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#auDateOfBirth-field-container" id="cta-auDateOfBirth">Authorized User\'s Date of Birth</a>', !acquisitionData.authorizedUserDateOfBirthDay.value && !acquisitionData.authorizedUserDateOfBirthMonth.value && !acquisitionData.authorizedUserDateOfBirthYear.value ? 'missingValue' : null) : 'Please provide the <a href="#auDateOfBirth-field-container" id="cta-auDateOfBirth">Authorized User\'s Date of Birth (MM-DD-YYYY)</a>',
        authorizedUserSpouse:
            isNewErrorMessagingFlagActive ? 'Select if <a href="#auSpouse-field-container" id="cta-auSpouse">Authorized User is your spouse</a>' : 'Please select if <a href="#auSpouse-field-container" id="cta-auSpouse">Authorized User is your spouse</a>',
        AddrAndAptNumMatch: 'Mailing address cannot be the same as apt/unit#',
        HomeAddrAndAptNumMatch: 'Home address cannot be the same as apt/unit#',
    });
